import { useMemo, useState } from 'react'

import { ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'

import { ImageComponent } from '@apps-common/components/common'
import { ProfileUserInitials } from '@apps-common/components/common/user'
import { initialsFromString } from '@apps-common/helpers/initials-from-string'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import classNames from 'classnames'
import { FaShieldHalved } from 'react-icons/fa6'
import { UnitUserType } from '../Index'
import { MyMonitoringUserModifyModal } from '../modals/my-monitoring-modify-modal'
import { SystemUserModifyModal } from '../modals/system-user-modify-modal'

interface IProps {
  index: number
  type: UnitUserType
  unitId: string
  ipAddress: string
  userId: number
  profileImage: string
  name: string
  email?: string
  zone: string
  partition?: string
  disabled?: boolean
  isAdmin?: boolean
}

export const UserListCard: React.FC<IProps> = ({
  index,
  type,
  unitId,
  userId,
  ipAddress,
  profileImage,
  name,
  email,
  zone,
  partition,
  disabled,
  isAdmin
}) => {
  const [brokenImg, setBrokenImg] = useState<boolean>(false)
  const btnColor = useProjectHook('bg-brand text-on-brand', 'bg-ctx-mm-brand text-ctx-on-mm-brand')

  const ModalComponent = useMemo(() => {
    if (type === 'system') {
      return SystemUserModifyModal
    }

    return MyMonitoringUserModifyModal
  }, [type])

  return (
    <ModalButton
      component={ModalComponent}
      disabled={disabled}
      componentProps={{ unit: { unitId, ipAddress }, userId }}
      classes='relative w-full !bg-surface text-text rounded-[30px] px-5 py-3 border border-border !opacity-100'
      enterAnimation={modalSlideUpEnterAnimation}
      leaveAnimation={modalSlideUpLeaveAnimation}
    >
      <div className='text-left relative flex items-start justify-start grow gap-[10px] sm:gap-[20px]'>
        <div className='mt-[5px]'>
          {!profileImage && <ProfileUserInitials classes='sm:w-[40px] sm:h-[40px]' textClasses='sm:!text-[1rem]' name={name} />}
          {profileImage && (
            <ImageComponent
              src={profileImage}
              alt='User picture'
              imageClasses={classNames(
                'w-[40px] h-[40px] sm:w-[50px] sm:h-[50px] overflow-hidden rounded-[20px] sm:rounded-[25px]',
                (!profileImage || brokenImg) && 'bg-surface-2'
              )}
              errorComponent={
                <div className='jost-bold uppercase mb-[2px] ml-[2px] flex items-center justify-center w-[40px] h-[40px] sm:w-[50px] sm:h-[50px] overflow-hidden rounded-[20px] sm:rounded-[25px] bg-surface-2'>
                  {initialsFromString(name)}
                </div>
              }
              errorIconClasses=''
            />
          )}
        </div>
        <div className='flex flex-col gap-[10px] grow overflow-hidden'>
          <div>
            <p className='jost-light text-nowrap text-ellipsis overflow-hidden'>{email || name}</p>
            {email && <p className='jost text-[0.8em] sm:text-[0.9em] text-nowrap text-ellipsis overflow-hidden'>{name}</p>}
          </div>
          <div className='flex items-center justify-between flex-wrap gap-[10px]'>
            <div className='flex items-center justify-start gap-[10px] text-[0.7em] md:text-[0.8em] leading-[1em] text-left'>
              <div className='jost flex items-center justify-between bg-text/[0.1] pl-[8px] pr-[2px] py-[2px] gap-[5px] rounded-lg'>
                <div>Zone</div>
                <div className='bg-surface-2 px-[5px] py-[2px] rounded-lg'>{zone}</div>
              </div>
              {partition && partition?.length > 0 && (
                <div className='jost flex items-center justify-between bg-text/[0.1] pl-[8px] pr-[2px] py-[2px] gap-[5px] rounded-lg'>
                  <div>Partition</div>
                  <div className='bg-surface-2 px-[5px] py-[2px] rounded-lg'>{partition}</div>
                </div>
              )}
            </div>
            {isAdmin && (
              <div className='bg-text/[0.1] text-text text-[0.75rem] flex items-center justify-end gap-1 jost-light px-[8px] py-[2px] rounded-lg'>
                <FaShieldHalved /> Admin
              </div>
            )}
          </div>
        </div>
        {!disabled && (
          <div className={classNames(btnColor, 'ml-auto w-[36px] h-[36px] rounded-full flex items-center justify-center')}>
            <commonIcons.arrowAngled />
          </div>
        )}
      </div>
    </ModalButton>
  )
}
